import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import postService, { CreatePostData, UpdatePostData, Post, CreatePostGroupData } from '../../services/postService';
import mediaService from '../../services/mediaService';
import { useClient } from '../../contexts/ClientContext';
import { useSocialConnections } from '../../hooks/useSocialConnections';
import { MultiMediaFileSelector } from '../media/MultiMediaFileSelector';
import { MediaFile } from '../media/types';
import AuthenticatedImage from '../media/AuthenticatedImage';
import MobilePreview from './MobilePreview';
import {
  X,
  Hash,
  FileText,
  Image as ImageIcon,
  Calendar,
  Send,
  Save,
  Wand2,
  Target,
  Clock,
  Eye,
  ArrowRight,
  CheckCircle,
  Star,
  Bot
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { aiService } from '../../services/aiService';
import { Switch } from '../ui/switch';
// import CustomDatePicker from '../../components/ui/CustomDatePicker';
import { motion, AnimatePresence } from 'framer-motion';
import { useFeatureLimit } from '../../hooks/useUserTier';
import { useMonthlyPostsLimit, useScheduledQueueLimit } from '../../hooks/useSocialMediaLimits';
import { useTour } from '../../contexts/TourContext';
import { postFormSteps } from '../onboarding/tour-steps';
import TierUpgradePrompt from '../tier/TierUpgradePrompt';
import PublishButton from '../limits/PublishButton';
import { div } from 'framer-motion/client';
import { UpgradeModal } from '../modals/UpgradeModal';

interface PostFormData {
  title: string;
  content: string;
  media_files?: string[];
  scheduled_at?: string;
  status?: 'draft' | 'scheduled' | 'published';
  platforms?: string[];
}

interface PostFormProps {
  mode: 'create' | 'edit';
  onClose?: (isOpen: boolean) => void;
  onSuccess?: () => void;
}

const allPlatforms = [
  { id: 'facebook', name: 'Facebook' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'twitter', name: 'X' },
  { id: 'linkedin', name: 'LinkedIn' },
];

const contentTypes = {
  instagram: [
    { id: 'feed', name: 'Feed Post', description: 'Posts for your main feed (supports multiple images)' },
    { id: 'story', name: 'Story', description: 'Vertical content that disappears in 24h' },
    { id: 'reel', name: 'Reel', description: 'Short vertical videos' }
  ],
  facebook: [
    { id: 'feed', name: 'Feed Post', description: 'Posts for your timeline (supports multiple photos)' },
    { id: 'story', name: 'Story', description: 'Temporary vertical content' }
  ],
  youtube: [
    { id: 'video', name: 'Video', description: 'Upload video content to your YouTube channel' }
  ],
  twitter: [
    { id: 'tweet', name: 'Post', description: 'Standard X post (up to 4 images, 280 characters)' }
  ],
  linkedin: [
    { id: 'post', name: 'Post', description: 'Professional network post (supports multiple images)' }
  ]
};

const PostForm: React.FC<PostFormProps> = ({ mode, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { postId, postGroupId } = useParams<{ postId: string, postGroupId: string }>();
  const location = useLocation();
  const { currentClient, canCreate, canEdit } = useClient();
  const { connections, isConnected } = useSocialConnections();
  const { toast } = useToast();
  const { startTour, restartTour } = useTour();

  useEffect(() => {
    startTour(postFormSteps, 'postForm');
  }, [startTour]);

  // Feature limits for content generation
  const { canUseFeature: canGenerateContent, usage: contentUsage, limit: contentLimit, remaining: contentRemaining, isUnlimited: isContentUnlimited } = useFeatureLimit('content_generation');

  // Social media tier limits
  const monthlyLimit = useMonthlyPostsLimit();
  const queueLimit = useScheduledQueueLimit();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPost, setIsLoadingPost] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [selectedMediaFiles, setSelectedMediaFiles] = useState<MediaFile[]>([]);
  const [generatedHashtags, setGeneratedHashtags] = useState<string[]>([]);
  const [isGeneratingHashtags, setIsGeneratingHashtags] = useState(false);
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [captionOptions, setCaptionOptions] = useState<string[]>([]);

  // Function to extract caption content without strategy category
  const extractCaptionContent = (fullCaption: string): string => {
    // Remove strategy category in parentheses at the beginning
    const match = fullCaption.match(/^\([^)]+\)\s*(.+)$/);
    return match ? match[1].trim() : fullCaption.trim();
  };
  const [platformContent, setPlatformContent] = useState<{ [key: string]: string }>({});
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Smart analysis states
  const [isAnalyzingMedia, setIsAnalyzingMedia] = useState(false);
  const [platformRecommendations, setPlatformRecommendations] = useState<{
    recommended: string[];
    reasons: { [key: string]: string };
  }>({ recommended: [], reasons: {} });

  const [selectedContentTypes, setSelectedContentTypes] = useState<{ [key: string]: string[] }>({});
  const [previewPlatform, setPreviewPlatform] = useState<'instagram' | 'facebook' | 'youtube' | 'twitter'>('instagram');
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalData, setUpgradeModalData] = useState<{
    message: string;
    currentCount?: number;
    limit?: number;
  }>({ message: '' });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<PostFormData>();

  const watchedStatus = watch('status', 'draft');
  const watchedPlatforms = watch('platforms', []);

  // Filter platforms to only show connected ones
  const platforms = allPlatforms.filter(platform => isConnected(platform.id));

  useEffect(() => {
    if (watchedStatus !== 'scheduled') {
      setValue('scheduled_at', undefined);
    }
  }, [watchedStatus, setValue]);

  useEffect(() => {
    if (mode === 'edit' && postGroupId) {
      loadPostGroup(postGroupId);
    } else if (mode === 'edit' && postId) {
      loadPost(postId);
    }
  }, [mode, postId, postGroupId]);

  // Auto-switch preview platform when YouTube is selected
  useEffect(() => {
    if (watchedPlatforms.includes('youtube')) {
      setPreviewPlatform('youtube');
    } else if (watchedPlatforms.length > 0 && !watchedPlatforms.includes(previewPlatform)) {
      // If current preview platform is not in selected platforms, switch to first selected
      setPreviewPlatform(watchedPlatforms[0] as 'instagram' | 'facebook' | 'youtube' | 'twitter');
    }
  }, [watchedPlatforms, previewPlatform]);

  useEffect(() => {
    if (location.state?.selectedMediaIds) {
      loadMediaFiles(location.state.selectedMediaIds);
    }
    if (location.state?.arViewerUrl) {
      // Create a more engaging post content with the AR URL
      const arContent = `🚀 Experience this in Augmented Reality! 

See how this product looks in your own space with our AR viewer:
${location.state.arViewerUrl}`;
      setValue('content', arContent);
    }
  }, [location.state]);

  // NEW useEffect for auto-filling content with AR URL
  useEffect(() => {
    if (selectedMediaFiles.length > 0) {
      const firstFileWithArUrl = selectedMediaFiles.find(file => file.ar_viewer_url);
      if (firstFileWithArUrl && !watch('content')) { // Only fill if content is empty
        setValue('content', firstFileWithArUrl.ar_viewer_url);
      }
    }
  }, [selectedMediaFiles, setValue, watch]);

  useEffect(() => {
    if (!currentClient) {
      navigate('/app/posts');
    }
  }, [currentClient, navigate]);

  useEffect(() => {
    setValue('media_files', selectedMediaFiles.map(file => file.id));

    // Trigger smart analysis when media files change
    if (selectedMediaFiles.length > 0) {
      analyzeMediaForPlatforms();
    } else {
      // Reset recommendations when no media
      setPlatformRecommendations({ recommended: [], reasons: {} });
      setSelectedContentTypes({});
    }
  }, [selectedMediaFiles, setValue]);

  // Smart media analysis function
  const analyzeMediaForPlatforms = async () => {
    if (selectedMediaFiles.length === 0) return;

    setIsAnalyzingMedia(true);

    // Simulate analysis delay for better UX
    await new Promise(resolve => setTimeout(resolve, 1500));

    const primaryFile = selectedMediaFiles[0];
    const imageCount = selectedMediaFiles.length;
    const aspectRatio = primaryFile.width && primaryFile.height
      ? primaryFile.width / primaryFile.height
      : 1;

    const recommendations: string[] = [];
    const reasons: { [key: string]: string } = {};
    const defaultContentTypes: { [key: string]: string[] } = {};

    // Handle videos with smart platform selection
    if (primaryFile.media_type === 'video') {
      // Always recommend YouTube for videos if connected
      if (isConnected('youtube')) {
        recommendations.push('youtube');
        reasons.youtube = 'Perfect for YouTube - supports all video formats and lengths';
        defaultContentTypes.youtube = ['video'];
      }

      // For vertical videos (YouTube Shorts format)
      if (aspectRatio < 0.8) {
        // Note: Instagram doesn't support direct video uploads via API
        // Only recommend Facebook for vertical video stories
        if (isConnected('facebook')) {
          recommendations.push('facebook');
          reasons.facebook = 'Good for Facebook video Stories (vertical format)';
          defaultContentTypes.facebook = ['story'];
        }
      }
      // For horizontal videos (traditional format)
      else if (aspectRatio > 1.5) {
        if (isConnected('facebook')) {
          recommendations.push('facebook');
          reasons.facebook = 'Great for Facebook video posts (horizontal format)';
          defaultContentTypes.facebook = ['feed'];
        }
      }
      // For square videos
      else if (Math.abs(aspectRatio - 1) < 0.2) {
        // Note: Instagram doesn't support direct video uploads via API
        if (isConnected('facebook')) {
          recommendations.push('facebook');
          reasons.facebook = 'Good for Facebook video posts (square format)';
          defaultContentTypes.facebook = ['feed'];
        }
      }

      // Set recommendations and auto-select recommended platforms for videos
      console.log('🎬 Video analysis - Recommended platforms:', recommendations);
      setPlatformRecommendations({ recommended: recommendations, reasons });
      setSelectedContentTypes(defaultContentTypes);
      
      // Auto-select the recommended platforms
      setValue('platforms', recommendations);
      console.log('🎬 Video analysis - Auto-selected platforms:', recommendations);
      
      setIsAnalyzingMedia(false);
      return;
    }

    // Handle multiple images first
    if (imageCount > 1) {
      if (imageCount <= 10) {
        // Multiple images in feed posts - only recommend connected platforms
        const multiImagePlatforms = ['instagram', 'facebook', 'twitter', 'linkedin'].filter(p => isConnected(p));
        recommendations.push(...multiImagePlatforms);

        if (isConnected('instagram')) {
          reasons.instagram = `Perfect for Instagram Feed Post with ${imageCount} images (carousel style)`;
          defaultContentTypes.instagram = ['feed'];
        }
        if (isConnected('facebook')) {
          reasons.facebook = `Great for Facebook Feed Post with ${imageCount} photos`;
          defaultContentTypes.facebook = ['feed'];
        }
        if (isConnected('linkedin')) {
          reasons.linkedin = `Ideal for LinkedIn Post with ${imageCount} images`;
          defaultContentTypes.linkedin = ['post'];
        }

        // Add Twitter for multiple images if not too many and connected
        if (imageCount <= 4 && isConnected('twitter')) {
          recommendations.push('twitter');
          reasons.twitter = `Good for Twitter with ${imageCount} images in one tweet`;
          defaultContentTypes.twitter = ['tweet'];
        }
      } else {
        // Too many images for single posts - suggest story series for connected platforms
        const storyPlatforms = ['instagram', 'facebook'].filter(p => isConnected(p));
        recommendations.push(...storyPlatforms);

        if (isConnected('instagram')) {
          reasons.instagram = `Perfect for Instagram Story series (${imageCount} slides)`;
          defaultContentTypes.instagram = ['story'];
        }
        if (isConnected('facebook')) {
          reasons.facebook = `Great for Facebook Story series (${imageCount} slides)`;
          defaultContentTypes.facebook = ['story'];
        }
      }
    } else {
      // Single image - analyze aspect ratio and only recommend connected platforms
      if (Math.abs(aspectRatio - 1) < 0.1) {
        // Square (1:1)
        const squarePlatforms = ['instagram', 'facebook', 'twitter'].filter(p => isConnected(p));
        recommendations.push(...squarePlatforms);

        if (isConnected('instagram')) {
          reasons.instagram = 'Perfect square format for Instagram feed';
          defaultContentTypes.instagram = ['feed'];
        }
        if (isConnected('facebook')) {
          reasons.facebook = 'Great for Facebook posts';
          defaultContentTypes.facebook = ['feed'];
        }
      } else if (aspectRatio < 0.8) {
        // Vertical/Portrait
        const verticalPlatforms = ['instagram', 'facebook', 'twitter'].filter(p => isConnected(p));
        recommendations.push(...verticalPlatforms);

        if (isConnected('instagram')) {
          if (primaryFile.media_type === 'video') {
            reasons.instagram = 'Ideal for Instagram Reels and Stories';
            defaultContentTypes.instagram = ['reel', 'story'];
          } else {
            reasons.instagram = 'Great for Instagram Stories';
            defaultContentTypes.instagram = ['story'];
          }
        }
        if (isConnected('facebook')) {
          reasons.facebook = 'Perfect for Facebook Stories';
          defaultContentTypes.facebook = ['story'];
        }
      } else if (aspectRatio > 1.5) {
        // Horizontal/Landscape
        const landscapePlatforms = ['twitter', 'facebook', 'linkedin'].filter(p => isConnected(p));
        recommendations.push(...landscapePlatforms);

        if (isConnected('twitter')) {
          reasons.twitter = 'Optimal landscape format for Twitter';
          defaultContentTypes.twitter = ['tweet'];
        }
        if (isConnected('facebook')) {
          reasons.facebook = 'Great for Facebook timeline posts';
          defaultContentTypes.facebook = ['feed'];
        }
        if (isConnected('linkedin')) {
          reasons.linkedin = 'Professional format for LinkedIn';
          defaultContentTypes.linkedin = ['post'];
        }
      } else {
        // Portrait (4:5)
        const portraitPlatforms = ['instagram', 'facebook', 'twitter'].filter(p => isConnected(p));
        recommendations.push(...portraitPlatforms);

        if (isConnected('instagram')) {
          reasons.instagram = 'Perfect portrait ratio for Instagram feed';
          defaultContentTypes.instagram = ['feed'];
        }
        if (isConnected('facebook')) {
          reasons.facebook = 'Great for Facebook posts';
          defaultContentTypes.facebook = ['feed'];
        }
      }
    }

    // Auto-select recommended platforms
    setValue('platforms', recommendations);
    setPlatformRecommendations({ recommended: recommendations, reasons });
    setSelectedContentTypes(defaultContentTypes);



    setIsAnalyzingMedia(false);
  };

  useEffect(() => {
    if (watchedPlatforms && watchedPlatforms.length > 0) {
      setActiveTab(watchedPlatforms[0]);
      
      // Initialize platformContent for each platform if not already set
      const currentContent = watch('content') ?? '';
      const newPlatformContent = { ...platformContent };
      let needsUpdate = false;
      
      watchedPlatforms.forEach(platform => {
        if (!(platform in newPlatformContent)) {
          newPlatformContent[platform] = currentContent;
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        setPlatformContent(newPlatformContent);
      }
    } else {
      setActiveTab(null);
    }
  }, [watchedPlatforms]);

  const loadPostGroup = async (id: string) => {
    try {
      setIsLoadingPost(true);
      const postGroupData = await postService.getPostGroup(id);
      setValue('title', postGroupData.title);
      setValue('status', postGroupData.status);
      if (postGroupData.scheduled_at) {
        setValue('scheduled_at', new Date(postGroupData.scheduled_at).toISOString().slice(0, 16));
      }

      const platforms = postGroupData.posts.flatMap(p => p.platforms || []);
      setValue('platforms', platforms);

      if (postGroupData.posts.length > 0) {
        const firstPost = postGroupData.posts[0];
        setValue('content', firstPost.content);
        if (firstPost.media_files && firstPost.media_files.length > 0) {
          loadMediaFiles(firstPost.media_files);
        }

        const platformContent: { [key: string]: string } = {};
        let isCustom = false;
        for (const post of postGroupData.posts) {
          if (post.platforms && post.platforms.length > 0) {
            const platform = post.platforms[0];
            if (post.content !== firstPost.content) {
              isCustom = true;
            }
            platformContent[platform] = post.content;
          }
        }
        setPlatformContent(platformContent);
      }

    } catch (err: any) {
      setError('Failed to load post group data');
      console.error('Failed to load post group:', err);
    } finally {
      setIsLoadingPost(false);
    }
  };

  const loadPost = async (id: string) => {
    try {
      setIsLoadingPost(true);
      const postData = await postService.getPost(id);
      setPost(postData);

      // Populate form with post data
      setValue('title', postData.title);
      setValue('content', postData.content);
      
      // Debug: Log the loaded post data
      console.log('PostForm: Loading existing post data:', {
        postId: postData.id,
        title: postData.title,
        content: postData.content,
        contentLength: postData.content?.length || 0,
        status: postData.status
      });
      
      setValue('status', postData.status);
      if (postData.scheduled_at) {
        setValue('scheduled_at', new Date(postData.scheduled_at).toISOString().slice(0, 16));
      }
      if (postData.platforms) {
        setValue('platforms', postData.platforms);
      }

      if (postData.media_files && postData.media_files.length > 0) {
        loadMediaFiles(postData.media_files);
      }

    } catch (err: any) {
      setError('Failed to load post data');
      console.error('Failed to load post:', err);
    } finally {
      setIsLoadingPost(false);
    }
  };

  const loadMediaFiles = async (mediaFileIds: string[]) => {
    if (!currentClient) return;
    try {
      const mediaFilesData = await Promise.all(
        mediaFileIds.map(fileId => mediaService.getMediaFile(fileId, currentClient.id))
      );
      setSelectedMediaFiles(mediaFilesData);
    } catch (error) {
      console.error("Failed to load media files", error);
      setError("Failed to load associated media files.");
    }
  }

  const handleGenerateHashtags = async () => {
    // Check feature limits before generating
    if (!canGenerateContent && !isContentUnlimited) {
      toast({
        title: "Feature Limit Reached",
        description: `You've used ${contentUsage}/${contentLimit} content generations this month. Upgrade to continue.`,
        variant: "destructive",
      });
      return;
    }

    const content = watchedPlatforms.length > 1 && activeTab ? platformContent[activeTab] || '' : watch('content');
    const mediaFileId = selectedMediaFiles.length > 0 ? selectedMediaFiles[0].id : undefined;

    if (!content && !mediaFileId) {
      toast({
        title: "No Content",
        description: "Please enter some content or select an image to generate hashtags.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingHashtags(true);
    try {
      let allHashtags: string[] = [];
      if (mediaFileId) {
        const response = await aiService.generateHashtagsFromImage(mediaFileId, content);
        allHashtags = [...allHashtags, ...response.hashtags];
      } else if (content) {
        const response = await aiService.generateHashtagsFromText(content);
        allHashtags = [...allHashtags, ...response.hashtags];
      }

      const uniqueHashtags = [...new Set(allHashtags)];
      setGeneratedHashtags(uniqueHashtags);

      toast({
        title: "Hashtags Generated",
        description: `Found ${uniqueHashtags.length} relevant hashtags.`,
      });
    } catch (error) {
      console.error("Error generating hashtags:", error);
      toast({
        title: "Error",
        description: "Failed to generate hashtags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingHashtags(false);
    }
  };

  const handleGenerateCaption = async () => {
    // Check feature limits before generating
    if (!canGenerateContent && !isContentUnlimited) {
      toast({
        title: "Feature Limit Reached",
        description: `You've used ${contentUsage}/${contentLimit} content generations this month. Upgrade to continue.`,
        variant: "destructive",
      });
      return;
    }

    const mediaFileId = selectedMediaFiles.length > 0 ? selectedMediaFiles[0].id : undefined;

    if (!mediaFileId) {
      toast({
        title: "No Image Selected",
        description: "Please select an image to generate a caption.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingCaption(true);
    setCaptionOptions([]);
    try {
      const response = await aiService.generateCaptionFromImage(mediaFileId);

      if (response.caption) {
        const options = response.caption.split('\n').filter(line => line.trim() !== '');
        setCaptionOptions(options);
        toast({
          title: "Captions Generated",
          description: "Choose a caption from the suggestions below.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate a caption for the image.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating caption:", error);
      toast({
        title: "Error",
        description: "Failed to generate caption. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingCaption(false);
    }
  };

  const handleOptimizeForAllPlatforms = async () => {
    const baseContent = watch('content') || '';
    if (!baseContent.trim()) {
      toast({
        title: "No Base Content",
        description: "Please enter some content first to optimize for platforms.",
        variant: "destructive",
      });
      return;
    }

    const selectedPlatforms = watchedPlatforms || [];
    if (selectedPlatforms.length === 0) {
      toast({
        title: "No Platforms Selected",
        description: "Please select at least one platform to optimize content.",
        variant: "destructive",
      });
      return;
    }

    // If only one platform, just keep the content as is
    if (selectedPlatforms.length === 1) {
      toast({
        title: "Content Ready",
        description: "Content is ready for your selected platform.",
      });
      return;
    }

    // Optimize for multiple platforms
    const newPlatformContent = { ...platformContent };

    selectedPlatforms.forEach(platform => {
      let optimizedContent = baseContent;

      // Platform-specific optimizations
      switch (platform) {
        case 'twitter':
          if (baseContent.length > 280) {
            // Smart truncation - try to keep complete sentences
            const sentences = baseContent.split('. ');
            let truncated = '';
            for (const sentence of sentences) {
              if ((truncated + sentence + '. ').length <= 277) {
                truncated += sentence + '. ';
              } else {
                break;
              }
            }
            optimizedContent = truncated.trim() || baseContent.substring(0, 277) + '...';
          }
          break;

        case 'instagram':
          // Add line breaks for better readability
          if (!baseContent.includes('\n\n')) {
            optimizedContent = baseContent.replace(/\. /g, '.\n\n');
          }
          break;

        case 'linkedin':
          // More professional tone
          optimizedContent = baseContent.replace(/!/g, '.').replace(/😊|😍|🔥/g, '');
          break;

        case 'facebook':
          // Add engaging elements
          if (!baseContent.includes('?') && !baseContent.includes('What do you think')) {
            optimizedContent = baseContent + '\n\nWhat do you think? Let us know in the comments!';
          }
          break;
      }

      newPlatformContent[platform] = optimizedContent;
    });

    setPlatformContent(newPlatformContent);

    toast({
      title: "Content Optimized",
      description: `Content optimized for ${selectedPlatforms.length} platforms with platform-specific formatting.`,
    });
  };

  const onSubmit = async (data: PostFormData) => {
    if (!currentClient) return;

    // Debug: Log the raw form data to see what's being captured
    console.log('PostForm: Raw form data received:', data);
    console.log('PostForm: Content field specifically:', data.content);
    console.log('PostForm: Content length:', data.content?.length || 0);
    
    // Debug: Also log the current form state
    const currentFormValues = watch();
    console.log('PostForm: Current form values from watch():', currentFormValues);
    console.log('PostForm: Current content from watch():', currentFormValues.content);

    // Convert local datetime to UTC for scheduled_at
    if (data.scheduled_at && data.status === 'scheduled') {
      // The datetime-local input gives us a string like "2025-09-20T16:30"
      // We need to treat this as local time and convert to UTC
      const localDate = new Date(data.scheduled_at);
      data.scheduled_at = localDate.toISOString();
      console.log('PostForm: Converting local time to UTC:', data.scheduled_at);
    }

    if (mode === 'create' && !canCreate()) {
      setError('You do not have permission to create posts');
      return;
    }

    // Check tier limits before proceeding
    if (data.status === 'published' && !monthlyLimit.canPublish) {
      toast({
        title: "Monthly Content Limit Reached",
        description: `You've reached your monthly content limit (${monthlyLimit.current_count}/${monthlyLimit.limit}). Upgrade your plan for higher limits.`,
        variant: "destructive",
      });
      return;
    }

    if (data.status === 'scheduled' && !queueLimit.canSchedule) {
      toast({
        title: "Scheduled Posts Queue Full",
        description: `Your scheduled posts queue is full (${queueLimit.current_count}/${queueLimit.limit}). Upgrade your plan for more scheduled posts.`,
        variant: "destructive",
      });
      return;
    }

    if (mode === 'create' && selectedMediaFiles.length === 0) {
      toast({
        title: "Media File Required",
        description: "Please select at least one media file for your post.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    if (mode === 'edit' && !canEdit()) {
      setError('You do not have permission to edit posts');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (mode === 'create') {
        const platformsToPost = data.platforms || [];
        const posts: CreatePostData[] = [];

        // Create posts for each platform and content type combination
        platformsToPost.forEach(platform => {
          const contentTypesForPlatform = selectedContentTypes[platform] || ['feed'];

          contentTypesForPlatform.forEach(contentType => {
            let adaptedContent = platformContent[platform] || data.content;

            // Apply platform-specific content adaptation
            if (platform === 'twitter' && adaptedContent && adaptedContent.length > 280) {
              adaptedContent = adaptedContent.substring(0, 277) + '...';
            }

            posts.push({
              title: `${data.title} - ${platform} ${contentType}`,
              content: adaptedContent,
              platforms: [platform],
              media_files: selectedMediaFiles.map(f => f.id),
              // Add content type metadata
              metadata: {
                contentType: contentType,
                platform: platform
              }
            });
          });
        });

        const postGroupData: CreatePostGroupData = {
          title: data.title,
          status: data.status || 'draft',
          scheduled_at: data.scheduled_at,
          posts: posts,
        };

        const createdPostGroup = await postService.createPostGroup(postGroupData);

        // If status is 'published', immediately publish to social media platforms
        if (data.status === 'published') {
          try {
            // Wait a moment for the posts to be fully created
            await new Promise(resolve => setTimeout(resolve, 500));

            const publishPromises = createdPostGroup.posts.map(async (post) => {
              if (selectedMediaFiles.length > 0) {
                // Get all media file URLs for carousel support
                const imageUrls = selectedMediaFiles.map(file => file.file_url);
                const content = platformContent[post.platforms?.[0]] || data.content;
                const platform = post.platforms?.[0];
                const contentType = post.metadata?.contentType || 'feed';

                if (imageUrls.length > 0 && post.id && platform) {
                  return postService.publishPost(post.id, imageUrls, content, platform, contentType);
                }
              }
              return null;
            });

            const validPromises = publishPromises.filter(Boolean);
            if (validPromises.length > 0) {
              await Promise.all(validPromises);

              toast({
                title: "Posts Published",
                description: "Your posts have been created and published to social media successfully.",
              });
              
              // Refresh tier limits after successful publishing
              monthlyLimit.refetch();
            } else {
              toast({
                title: "Posts Created",
                description: "Posts were created but could not be published. Please check media files and try publishing manually.",
                variant: "destructive",
              });
            }
          } catch (publishError: any) {
            console.error('Failed to publish posts:', publishError);
            
            // Check if it's a tier limit error
            if (publishError?.response?.status === 403 && publishError?.response?.data?.detail) {
              toast({
                title: "Publishing Failed",
                description: publishError.response.data.detail,
                variant: "destructive",
              });
            } else {
              toast({
                title: "Posts Created, Publishing Failed",
                description: "Posts were created but failed to publish to social media. You can publish them manually.",
                variant: "destructive",
              });
            }
          }
        } else {
          toast({
            title: "Post Group Created",
            description: "Your post group has been created successfully.",
          });
          
          // Refresh tier limits after successful creation
          if (data.status === 'scheduled') {
            queueLimit.refetch();
          }
        }

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/app/posts');
        }
      } else if (mode === 'edit' && postGroupId) {
        const platformsToPost = data.platforms || [];
        const posts: CreatePostData[] = [];

        // Create posts for each platform and content type combination
        platformsToPost.forEach(platform => {
          const contentTypesForPlatform = selectedContentTypes[platform] || ['feed'];

          contentTypesForPlatform.forEach(contentType => {
            let adaptedContent = platformContent[platform] || data.content;

            // Apply platform-specific content adaptation
            if (platform === 'twitter' && adaptedContent && adaptedContent.length > 280) {
              adaptedContent = adaptedContent.substring(0, 277) + '...';
            }

            posts.push({
              title: `${data.title} - ${platform} ${contentType}`,
              content: adaptedContent,
              platforms: [platform],
              media_files: selectedMediaFiles.map(f => f.id),
              // Add content type metadata
              metadata: {
                contentType: contentType,
                platform: platform
              }
            });
          });
        });

        const postGroupData: CreatePostGroupData = {
          title: data.title,
          status: data.status || 'draft',
          scheduled_at: data.scheduled_at,
          posts: posts,
        };

        const updatedPostGroup = await postService.updatePostGroup(postGroupId, postGroupData);

        // If status is 'published', immediately publish to social media platforms
        if (data.status === 'published') {
          try {
            const publishPromises = updatedPostGroup.posts.map(async (post) => {
              if (selectedMediaFiles.length > 0) {
                // Get all media file URLs for carousel support
                const imageUrls = selectedMediaFiles.map(file => file.file_url);
                const content = platformContent[post.platforms?.[0]] || data.content;
                const platform = post.platforms?.[0];
                const contentType = post.metadata?.contentType || 'feed';

                if (imageUrls.length > 0 && post.id && platform) {
                  return postService.publishPost(post.id, imageUrls, content, platform, contentType);
                }
              }
              return null;
            });

            const validPromises = publishPromises.filter(Boolean);
            if (validPromises.length > 0) {
              await Promise.all(validPromises);

              toast({
                title: "Posts Published",
                description: "Your posts have been updated and published to social media successfully.",
              });
              
              // Refresh tier limits after successful publishing
              monthlyLimit.refetch();
            } else {
              toast({
                title: "Posts Updated",
                description: "Posts were updated but could not be published. Please check media files and try publishing manually.",
                variant: "destructive",
              });
            }
          } catch (publishError: any) {
            console.error('Failed to publish posts:', publishError);
            
            // Check if it's a tier limit error
            if (publishError?.response?.status === 403 && publishError?.response?.data?.detail) {
              toast({
                title: "Publishing Failed",
                description: publishError.response.data.detail,
                variant: "destructive",
              });
            } else {
              toast({
                title: "Posts Updated, Publishing Failed",
                description: "Posts were updated but failed to publish to social media. You can publish them manually.",
                variant: "destructive",
              });
            }
          }
        } else {
          toast({
            title: "Post Group Updated",
            description: "Your post group has been updated successfully.",
          });
          
          // Refresh tier limits after successful update
          if (data.status === 'scheduled') {
            queueLimit.refetch();
          }
        }

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/app/posts');
        }
      } else if (mode === 'edit' && postId) {
        // Update logic for single posts
        const postData = { ...data, media_files: selectedMediaFiles.map(f => f.id) };
        
        // Debug logging to verify content is being saved
        console.log('PostForm: Updating post with data:', {
          postId,
          title: postData.title,
          content: postData.content,
          contentLength: postData.content?.length || 0,
          status: postData.status,
          scheduled_at: postData.scheduled_at,
          media_files: postData.media_files,
          fullPostData: postData
        });
        
        const updatedPost = await postService.updatePost(postId, postData);

        // If status is 'published', immediately publish to social media platforms
        if (data.status === 'published') {
          try {
            if (selectedMediaFiles.length > 0) {
              // Get all media file URLs for carousel support
              const imageUrls = selectedMediaFiles.map(file => file.file_url);

              if (imageUrls.length > 0) {
                // For single post editing, we need to determine platform and content type
                const platform = data.platforms?.[0] || 'instagram';
                const contentType = selectedContentTypes[platform]?.[0] || 'feed';
                await postService.publishPost(postId, imageUrls, data.content, platform, contentType);

                toast({
                  title: "Post Published",
                  description: "Your post has been updated and published to social media successfully.",
                });
                
                // Refresh tier limits after successful publishing
                monthlyLimit.refetch();
              } else {
                // Revert status to draft if no media URL
                await postService.updatePost(postId, { ...data, status: 'draft', media_files: selectedMediaFiles.map(f => f.id) });
                toast({
                  title: "Post Updated, No Media URL",
                  description: "Post was updated but media file doesn't have a valid URL for publishing.",
                  variant: "destructive",
                });
              }
            } else {
              // Revert status to draft if no media
              await postService.updatePost(postId, { ...data, status: 'draft', media_files: selectedMediaFiles.map(f => f.id) });
              toast({
                title: "Post Updated, No Media",
                description: "Post was updated but cannot be published without media.",
                variant: "destructive",
              });
            }
          } catch (publishError: any) {
            console.error('Failed to publish post:', publishError);
            
            // Revert status to draft since publishing failed
            try {
              await postService.updatePost(postId, { ...data, status: 'draft', media_files: selectedMediaFiles.map(f => f.id) });
            } catch (revertError) {
              console.error('Failed to revert post status:', revertError);
            }
            
            // Check if it's a tier limit error
            if (publishError?.response?.status === 403 && publishError?.response?.data?.detail) {
              const errorDetail = publishError.response.data.detail;
              
              // Check if it's a monthly post limit error
              if (typeof errorDetail === 'object' && errorDetail.error === 'monthly_post_limit_exceeded') {
                setUpgradeModalData({
                  message: errorDetail.message,
                  currentCount: errorDetail.current_count,
                  limit: errorDetail.limit
                });
                setShowUpgradeModal(true);
              } else {
                // Other 403 errors
                const message = typeof errorDetail === 'string' ? errorDetail : errorDetail.message || 'Publishing failed';
                toast({
                  title: "Publishing Failed",
                  description: message,
                  variant: "destructive",
                });
              }
            } else {
              toast({
                title: "Post Updated, Publishing Failed",
                description: "Post was updated but failed to publish to social media. Status reverted to draft.",
                variant: "destructive",
              });
            }
          }
        } else {
          toast({
            title: "Post Updated",
            description: "Your post content and settings have been saved successfully.",
          });
          
          // Refresh tier limits after successful update
          if (data.status === 'scheduled') {
            queueLimit.refetch();
          }
        }

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/app/posts');
        }
      }
    } catch (err: any) {
      // Handle 403 errors (tier limits) specially
      if (err.response?.status === 403) {
        const detail = err.response.data?.detail || '';
        if (detail.includes('Monthly') || detail.includes('limit') || detail.includes('queue')) {
          toast({
            title: "Tier Limit Reached",
            description: `${detail} Click here to upgrade your plan.`,
            variant: "destructive",
          });
          
          // Show upgrade option after a short delay
          setTimeout(() => {
            if (window.confirm('Would you like to upgrade your plan now?')) {
              navigate('/app/settings/billing');
            }
          }, 2000);
          setError(null); // Don't show generic error
          setIsLoading(false);
          return;
        }
      }
      
      let errorMessage = `Failed to ${mode} post`;
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentClient) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a client to manage posts.</p>
      </div>
    );
  }

  if (isLoadingPost) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-slate-900 dark:text-white p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <motion.div
        className="mb-6 lg:mb-8"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="relative inline-block w-full">
          <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl sm:rounded-3xl blur-xl sm:blur-2xl"></div>
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 p-3 sm:p-4 bg-white/50 dark:bg-slate-800/30 rounded-xl sm:rounded-2xl border border-slate-200/50 dark:border-slate-600/30 backdrop-blur-sm">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-black bg-gradient-to-r from-gray-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200 leading-tight">
                {mode === 'create' ? 'Create New Post' : 'Edit Post'}
              </h1>
              <p className="text-slate-500 dark:text-slate-300 text-sm sm:text-base mt-1 leading-relaxed">
                {mode === 'create'
                  ? 'Craft engaging content for your social media audience'
                  : 'Update your post content and settings'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => restartTour(postFormSteps, 'postForm')}
              className="px-4 py-2 border border-slate-300/70 dark:border-slate-600/50 bg-white dark:bg-slate-700/50 hover:bg-slate-100/50 dark:hover:bg-slate-600/50 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-all duration-300 text-sm"
            >
              Tour
            </button>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 xl:grid-cols-12 gap-4 lg:gap-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        {/* Left Panel - Form */}
        <div className="xl:col-span-5 space-y-4">
          {error && (
            <motion.div
              className="p-4 bg-red-500/10 border border-red-500/30 rounded-2xl backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <p className="text-red-500 dark:text-red-400 font-medium">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6 lg:space-y-8">
            {/* Title Section */}
            <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
<div id="post-title" className="relative p-3">

                <div>
                  <label className="text-slate-700 dark:text-slate-300 font-medium mb-2 block text-xs">Post Title *</label>
                  <input
                    {...register('title', {
                      required: 'Title is required',
                      maxLength: {
                        value: 200,
                        message: 'Title must be less than 200 characters',
                      },
                    })}
                    type="text"
                    className="w-full px-3 py-2 bg-slate-100/50 dark:bg-slate-700/50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all placeholder-slate-400 text-slate-800 dark:text-slate-200 backdrop-blur-sm text-sm"
                    placeholder="Enter an engaging title..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    <div></div>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {watch('title')?.length || 0} / 200
                    </span>
                  </div>
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors.title.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Media Files Section */}
            <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
              <div id="media-upload" className="relative p-3">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center">
                    <ImageIcon className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200">Media</h3>
                </div>

                {location.state?.selectedMediaIds ? (
                  <div className="p-4 bg-slate-100/50 dark:bg-slate-700/30 rounded-xl">
                    {location.state?.generatedFromAdStudio && (
                      <div className="mb-3 p-2 bg-indigo-100/50 dark:bg-indigo-900/30 rounded-lg border border-indigo-200/50 dark:border-indigo-700/50">
                        <p className="text-xs text-indigo-700 dark:text-indigo-300 font-medium">
                          ✨ AI-Enhanced Image from Ad Studio
                        </p>
                      </div>
                    )}
                    {location.state?.generatedFromMediaLibrary && (
                      <div className="mb-3 p-2 bg-emerald-100/50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200/50 dark:border-emerald-700/50">
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                          📁 Selected from Media Library
                        </p>
                      </div>
                    )}
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="font-semibold">{selectedMediaFiles.length}</span> media file{selectedMediaFiles.length !== 1 ? 's' : ''} selected
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedMediaFiles.map(file => (
                        <span key={file.id} className="text-xs bg-slate-200/50 dark:bg-slate-600/50 px-2 py-1 rounded-lg">
                          {file.original_filename}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <MultiMediaFileSelector
                      selectedFiles={selectedMediaFiles}
                      onSelectionChange={setSelectedMediaFiles}
                    />
                    {selectedMediaFiles.length > 0 && (
                      <div className="p-4 bg-slate-100/50 dark:bg-slate-700/30 rounded-xl">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            <span className="font-semibold">{selectedMediaFiles.length}</span> media file{selectedMediaFiles.length !== 1 ? 's' : ''} selected
                          </p>
                          <button
                            type="button"
                            onClick={() => setSelectedMediaFiles([])}
                            className="text-xs text-red-500 hover:text-red-600 transition-colors"
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedMediaFiles.map(file => (
                            <div key={file.id} className="flex items-center space-x-1 text-xs bg-slate-200/50 dark:bg-slate-600/50 px-2 py-1 rounded-lg">
                              <span>{file.original_filename}</span>
                              <button
                                type="button"
                                onClick={() => setSelectedMediaFiles(selectedMediaFiles.filter(f => f.id !== file.id))}
                                className="text-red-500 hover:text-red-600 transition-colors ml-1"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Platforms Section */}
            <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
              <div id="platform-selection" className="relative p-3">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center">
                    <Target className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200">Platforms</h3>
                  {isAnalyzingMedia && (
                    <div className="flex items-center space-x-2 ml-auto">
                      <div className="w-3 h-3 border border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">Analyzing...</span>
                    </div>
                  )}
                </div>

                {platforms.length === 0 ? (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <Target className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm font-medium text-amber-800 dark:text-amber-200">No Connected Platforms</span>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                      Connect your social media accounts to start posting. Go to Settings → Social Connections to link your accounts.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/app/settings/social')}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg transition-colors"
                    >
                      Connect Platforms
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Smart Analysis Results */}
                    {platformRecommendations.recommended.length > 0 && !isAnalyzingMedia && (
                        <motion.div
                        className="mb-4 p-3 bg-emerald-100/50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200/50 dark:border-emerald-700/50"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            <div>
                              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Smart Analysis Complete</span>
                              <p className="text-xs text-emerald-600 dark:text-emerald-400">Recommended {platformRecommendations.recommended.length} connected platforms based on your media format</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 px-2 py-1 bg-emerald-200/50 dark:bg-emerald-800/30 rounded-full">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Optimized</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {platforms.map(platform => {
                        const isRecommended = platformRecommendations.recommended.includes(platform.id);
                        return (
                          <Controller
                            key={platform.id}
                            name="platforms"
                            control={control}
                            render={({ field }) => (
                              <label className="flex items-center space-x-2 p-3 transition-all cursor-pointer hover:bg-gray-50/50 dark:hover:bg-gray-800/30 rounded-lg">
                                <Switch
                                  checked={field.value?.includes(platform.id)}
                                  onCheckedChange={(checked) => {
                                    const currentValues = field.value || [];
                                    if (checked) {
                                      field.onChange([...currentValues, platform.id]);
                                    } else {
                                      field.onChange(currentValues.filter((id) => id !== platform.id));
                                      // Remove content types for deselected platform
                                      const newContentTypes = { ...selectedContentTypes };
                                      delete newContentTypes[platform.id];
                                      setSelectedContentTypes(newContentTypes);
                                    }
                                  }}
                                />
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-slate-900 dark:text-white">{platform.name}</span>
                                  {isRecommended && (
                                    <div className="flex items-center space-x-1">
                                      <Star className="w-3 h-3 text-amber-600 dark:text-white/70" />
                                      <span className="text-xs font-medium text-amber-700 dark:text-white/80">Recommended</span>
                                    </div>
                                  )}
                                </div>
                              </label>
                            )}
                          />
                        );
                      })}
                    </div>
                  </>
                )}


              </div>
            </div>

            {/* Content Types Section */}
            {watchedPlatforms && watchedPlatforms.length > 0 && (
              <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
                <div className="relative p-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center">
                      <Eye className="w-3 h-3 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200">Content Types</h3>
                  </div>

                  <div className="space-y-4">
                    {watchedPlatforms.map(platformId => {
                      const platform = platforms.find(p => p.id === platformId);
                      const availableTypes = contentTypes[platformId] || [];
                      const selectedTypes = selectedContentTypes[platformId] || [];

                      return (
                        <div key={platformId} className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{platform?.name}</span>
                            {platformRecommendations.recommended.includes(platformId) && (
                              <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                            {availableTypes.map(contentType => {
                              const isSelected = selectedTypes.includes(contentType.id);
                              const isRecommended = selectedContentTypes[platformId]?.includes(contentType.id);

                              // Only check for aspect ratio mismatch warnings if the content type is selected
                              const getAspectRatioWarning = () => {
                                if (!isSelected || selectedMediaFiles.length === 0) return null;
                                const primaryFile = selectedMediaFiles[0];
                                const aspectRatio = primaryFile.width && primaryFile.height
                                  ? primaryFile.width / primaryFile.height
                                  : 1;

                                // Define ideal aspect ratios for content types
                                const idealRatios = {
                                  story: { min: 0.5, max: 0.7, name: '9:16 (vertical)' },
                                  reel: { min: 0.5, max: 0.7, name: '9:16 (vertical)' },
                                  feed: { min: 0.8, max: 1.91, name: '1:1 to 1.91:1' },
                                  tweet: { min: 0.5, max: 2, name: 'flexible' },
                                  post: { min: 0.5, max: 2, name: 'flexible' }
                                };

                                const ideal = idealRatios[contentType.id];
                                if (ideal && (aspectRatio < ideal.min || aspectRatio > ideal.max)) {
                                  return `Image may be cropped (ideal: ${ideal.name})`;
                                }
                                return null;
                              };

                              const warning = getAspectRatioWarning();

                              return (
                                <motion.div
                                  key={contentType.id}
                                  className={`p-2.5 rounded-lg border cursor-pointer transition-all duration-300 relative ${isSelected && warning
                                    ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-300 dark:border-amber-600/50 shadow-md shadow-amber-500/10'
                                    : isSelected
                                      ? 'bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-indigo-300 dark:border-indigo-600/50 shadow-md shadow-indigo-500/10'
                                      : 'bg-white/80 dark:bg-slate-800/60 border-slate-200/60 dark:border-slate-600/40 hover:bg-slate-50/90 dark:hover:bg-slate-700/60 hover:border-slate-300/70 dark:hover:border-slate-500/60 hover:shadow-sm'
                                    }`}
                                  onClick={() => {
                                    const newContentTypes = { ...selectedContentTypes };

                                    if (isSelected) {
                                      // If clicking on already selected type, deselect it
                                      newContentTypes[platformId] = [];
                                    } else {
                                      // If clicking on unselected type, select only this one (replace any existing selection)
                                      newContentTypes[platformId] = [contentType.id];
                                    }

                                    setSelectedContentTypes(newContentTypes);
                                  }}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`text-xs font-semibold truncate ${isSelected && warning
                                      ? 'text-amber-700 dark:text-amber-300'
                                      : isSelected
                                        ? 'text-indigo-700 dark:text-indigo-300'
                                        : 'text-slate-800 dark:text-slate-200'
                                      }`}>
                                      {contentType.name}
                                    </span>
                                    <div className="flex items-center space-x-1 ml-1">
                                      {warning && (
                                        <div className="w-2.5 h-2.5 bg-amber-500 rounded-full flex items-center justify-center" title={warning}>
                                          <div className="w-1 h-1 bg-white rounded-full"></div>
                                        </div>
                                      )}
                                      {isRecommended && !warning && (
                                        <div className="flex items-center px-1.5 py-0.5 bg-blue-100/50 dark:bg-blue-900/30 rounded-full border border-blue-200/50 dark:border-blue-700/50">
                                          <Star className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                      )}
                                      {isSelected && !warning && (
                                        <div className="w-2.5 h-2.5 bg-blue-500 rounded-full flex items-center justify-center">
                                          <div className="w-1 h-1 bg-white rounded-full"></div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <p className={`text-xs leading-tight line-clamp-2 ${isSelected && warning
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : isSelected
                                      ? 'text-indigo-600 dark:text-indigo-400'
                                      : 'text-slate-600 dark:text-slate-400'
                                    }`}>
                                    {warning || contentType.description}
                                  </p>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Content Section */}
            <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
              <div id="content-editor" className="relative p-3 sm:p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center">
                    <Wand2 className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200">Content</h3>
                </div>

                {watchedPlatforms && watchedPlatforms.length > 1 ? (
                  <div className="space-y-4">
                    {/* Platform Tabs */}
                    <div className="flex space-x-2 p-1 bg-slate-100/50 dark:bg-slate-700/30 rounded-xl">
                      {watchedPlatforms.map(platformId => (
                        <button
                          key={platformId}
                          type="button"
                          onClick={() => setActiveTab(platformId)}
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === platformId
                            ? 'bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                            }`}
                        >
                          {platforms.find(p => p.id === platformId)?.name}
                        </button>
                      ))}
                    </div>

                    {/* Content Area */}
                    {activeTab && (
                      <div className="relative">
                        <textarea
                          value={platformContent[activeTab] ?? ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setPlatformContent({ ...platformContent, [activeTab]: newValue });
                            // Also sync to form state to keep them in sync
                            setValue('content', newValue);
                          }}
                          rows={4}
                          className="w-full px-4 py-3 bg-slate-100/50 dark:bg-slate-700/50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all placeholder-slate-400 text-slate-800 dark:text-slate-200 backdrop-blur-sm resize-none"
                          placeholder={`Write engaging content for ${platforms.find(p => p.id === activeTab)?.name}...`}
                        />
                        <div className="flex justify-between items-center mt-2">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={handleGenerateCaption}
                              disabled={isGeneratingCaption || selectedMediaFiles.length === 0}
                              className="px-3 py-1.5 bg-blue-500/90 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                            >
                              <Bot className="w-3 h-3" />
                              <span>{isGeneratingCaption ? 'Generating...' : 'AI Caption'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={handleGenerateHashtags}
                              disabled={isGeneratingHashtags}
                              className="px-3 py-1.5 bg-purple-500/90 hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                            >
                              <Hash className="w-3 h-3" />
                              <span>{isGeneratingHashtags ? 'Generating...' : 'AI Hashtags'}</span>
                            </button>
                            {(() => {
                              const hasBrandVoice = currentClient?.brand_voice;
                              const hasIndustry = currentClient?.industry;
                              const hasPrimaryGoal = currentClient?.primary_goal;
                              const hasTargetAudience = currentClient?.target_audience;
                              const hasBrandPersonality = currentClient?.brand_personality?.length > 0;
                              
                              const brandKitComplete = hasBrandVoice && hasIndustry && hasPrimaryGoal && hasTargetAudience && hasBrandPersonality;
                              const hasSomeBrandInfo = hasBrandVoice || hasIndustry || hasPrimaryGoal || hasTargetAudience || hasBrandPersonality;
                              
                              if (brandKitComplete) {
                                return (
                                  <div className="flex items-center space-x-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-md text-xs">
                                    <Target className="w-3 h-3" />
                                    <span>Brand-optimized</span>
                                  </div>
                                );
                              } else if (hasSomeBrandInfo) {
                                return (
                                  <div className="flex items-center space-x-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-md text-xs cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                                       onClick={() => window.open(`/app/clients/${currentClient?.id}/brand-kit`, '_blank')}>
                                    <Star className="w-3 h-3" />
                                    <span>Complete Brand Kit for better results</span>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex items-center space-x-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-xs cursor-pointer hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                                       onClick={() => window.open(`/app/clients/${currentClient?.id}/brand-kit`, '_blank')}>
                                    <Star className="w-3 h-3" />
                                    <span>Set up Brand Kit for personalized content</span>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                          <div className="flex items-center space-x-2">
                            {watchedPlatforms && watchedPlatforms.length > 1 && (
                              <button
                                type="button"
                                onClick={handleOptimizeForAllPlatforms}
                                className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5"
                              >
                                <Wand2 className="w-3 h-3" />
                                <span>Optimize for All Platforms</span>
                              </button>
                            )}
                            {activeTab === 'twitter' && (
                              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                Twitter: {(platformContent[activeTab] ?? '').length}/280
                              </span>
                            )}
                            {activeTab !== 'twitter' && (
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {(platformContent[activeTab] ?? '').length} / 2000
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="relative">
                      <textarea
                        {...register('content', {
                          maxLength: {
                            value: 2000,
                            message: 'Content must be less than 2000 characters',
                          },
                        })}
                        rows={4}
                        className="w-full px-4 py-3 bg-slate-100/50 dark:bg-slate-700/50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all placeholder-slate-400 text-slate-800 dark:text-slate-200 backdrop-blur-sm resize-none"
                        placeholder="Write engaging content for your post..."
                      />
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={handleGenerateCaption}
                            disabled={isGeneratingCaption || selectedMediaFiles.length === 0}
                            className="px-3 py-1.5 bg-blue-500/90 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                          >
                            <Bot className="w-3 h-3" />
                            <span>{isGeneratingCaption ? 'Generating...' : 'AI Caption'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleGenerateHashtags}
                            disabled={isGeneratingHashtags}
                            className="px-3 py-1.5 bg-purple-500/90 hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5"
                          >
                            <Hash className="w-3 h-3" />
                            <span>{isGeneratingHashtags ? 'Generating...' : 'AI Hashtags'}</span>
                          </button>
                          {(() => {
                            const hasBrandVoice = currentClient?.brand_voice;
                            const hasIndustry = currentClient?.industry;
                            const hasPrimaryGoal = currentClient?.primary_goal;
                            const hasTargetAudience = currentClient?.target_audience;
                            const hasBrandPersonality = currentClient?.brand_personality?.length > 0;
                            
                            const brandKitComplete = hasBrandVoice && hasIndustry && hasPrimaryGoal && hasTargetAudience && hasBrandPersonality;
                            const hasSomeBrandInfo = hasBrandVoice || hasIndustry || hasPrimaryGoal || hasTargetAudience || hasBrandPersonality;
                            
                            if (brandKitComplete) {
                              return (
                                <div className="flex items-center space-x-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-md text-xs">
                                  <Target className="w-3 h-3" />
                                  <span>Brand-optimized</span>
                                </div>
                              );
                            } else if (hasSomeBrandInfo) {
                              return (
                                <div className="flex items-center space-x-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-md text-xs cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                                   onClick={() => window.open(`/app/clients/${currentClient?.id}/brand-kit`, '_blank')}>
                                  <Star className="w-3 h-3" />
                                  <span>Complete Brand Kit for better results</span>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex items-center space-x-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-xs cursor-pointer hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                                   onClick={() => window.open(`/app/clients/${currentClient?.id}/brand-kit`, '_blank')}>
                                  <Star className="w-3 h-3" />
                                  <span>Set up Brand Kit for personalized content</span>
                                </div>
                              );
                            }
                          })()}
                        </div>
                        <div className="flex items-center space-x-2">
                          {watchedPlatforms && watchedPlatforms.length > 1 && (
                            <button
                              type="button"
                              onClick={handleOptimizeForAllPlatforms}
                              className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5"
                            >
                              <Wand2 className="w-3 h-3" />
                              <span>Optimize for All Platforms</span>
                            </button>
                          )}
                          {watchedPlatforms?.includes('twitter') && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                              Twitter limit: {watch('content')?.length || 0}/280
                            </span>
                          )}
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {watch('content')?.length || 0} / 2000
                          </span>
                        </div>
                      </div>
                    </div>
                    {errors.content && (
                      <p className="text-sm text-red-500 dark:text-red-400 flex items-center space-x-1">
                        <X className="w-4 h-4" />
                        <span>{errors.content.message}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generated Captions Modal */}
            {captionOptions.length > 0 && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <motion.div
                  className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 w-full max-w-2xl max-h-[80vh] overflow-hidden"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: "spring", duration: 0.5 }}
                >
                  {/* Header */}
                  <div className="p-4 border-b border-slate-200/50 dark:border-slate-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                          <FileText className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-200">Choose Your Caption</h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">Select the strategy that fits your goals</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCaptionOptions([])}
                        className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-500 dark:text-slate-400"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Caption Options */}
                  <div className="p-4 max-h-96 overflow-y-auto">
                    <div className="grid gap-3">
                      {captionOptions.map((caption, index) => {
                        const match = caption.match(/^\(([^)]+)\)\s*(.+)$/);
                        if (match) {
                          const [, strategy, content] = match;
                          const strategyColors = {
                            // New strategy names
                            'increase_sales': 'bg-emerald-200 dark:bg-emerald-500/30 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-500/40',
                            'product_showcase': 'bg-purple-200 dark:bg-purple-500/30 text-purple-800 dark:text-purple-200 border border-purple-300 dark:border-purple-500/40',
                            'urgency_creator': 'bg-orange-200 dark:bg-orange-500/30 text-orange-800 dark:text-orange-200 border border-orange-300 dark:border-orange-500/40',
                            'social_proof': 'bg-blue-200 dark:bg-blue-500/30 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-500/40',
                            'lifestyle_integration': 'bg-pink-200 dark:bg-pink-500/30 text-pink-800 dark:text-pink-200 border border-pink-300 dark:border-pink-500/40',
                            'value_proposition': 'bg-cyan-200 dark:bg-cyan-500/30 text-cyan-800 dark:text-cyan-200 border border-cyan-300 dark:border-cyan-500/40',
                            // Old strategy names (fallback)
                            'engagement_driver': 'bg-emerald-200 dark:bg-emerald-500/30 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-500/40',
                            'brand_storyteller': 'bg-purple-200 dark:bg-purple-500/30 text-purple-800 dark:text-purple-200 border border-purple-300 dark:border-purple-500/40',
                            'conversion_focused': 'bg-orange-200 dark:bg-orange-500/30 text-orange-800 dark:text-orange-200 border border-orange-300 dark:border-orange-500/40',
                            'community_builder': 'bg-blue-200 dark:bg-blue-500/30 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-500/40',
                            'viral_potential': 'bg-pink-200 dark:bg-pink-500/30 text-pink-800 dark:text-pink-200 border border-pink-300 dark:border-pink-500/40'
                          };

                          return (
                            <div
                              key={index}
                              className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer group border border-slate-200/50 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600"
                              onClick={() => {
                                const cleanCaption = extractCaptionContent(caption);
                                setValue('content', cleanCaption);
                                // Also update platformContent if in multi-platform mode
                                if (watchedPlatforms.length > 1 && activeTab) {
                                  setPlatformContent({ ...platformContent, [activeTab]: cleanCaption });
                                }
                                setCaptionOptions([]);
                                toast({
                                  title: "Caption Applied",
                                  description: `${strategy.replace('_', ' ').toUpperCase()} caption added. Click "Optimize for All Platforms" to adapt it for each platform.`,
                                });
                              }}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${strategyColors[strategy] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'}`}>
                                    {strategy.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors leading-relaxed">
                                    {content.trim()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div
                              key={index}
                              className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer group border border-slate-200/50 dark:border-slate-700/50"
                              onClick={() => {
                                const cleanCaption = extractCaptionContent(caption);
                                setValue('content', cleanCaption);
                                // Also update platformContent if in multi-platform mode
                                if (watchedPlatforms.length > 1 && activeTab) {
                                  setPlatformContent({ ...platformContent, [activeTab]: cleanCaption });
                                }
                                setCaptionOptions([]);
                                toast({
                                  title: "Caption Applied",
                                  description: "Caption added. Click \"Optimize for All Platforms\" to adapt it for each platform.",
                                });
                              }}
                            >
                              <p className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">
                                {caption}
                              </p>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="p-4 border-t border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30">
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Click any caption to apply it to your post
                      </p>
                      <button
                        type="button"
                        onClick={() => setCaptionOptions([])}
                        className="px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg text-slate-700 dark:text-slate-300 text-sm font-medium transition-all duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Generated Hashtags Section */}
            {generatedHashtags.length > 0 && (
              <div id="hashtags" className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-rose-500/5"></div>
                <div className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                        <Hash className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-black dark:text-white">Generated Hashtags</h3>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          const hashtagString = generatedHashtags.join(' ');
                          const currentContent = watchedPlatforms.length > 1 && activeTab
                            ? platformContent[activeTab] ?? ''
                            : watch('content') ?? '';
                          const newContent = currentContent ? `${currentContent} ${hashtagString}` : hashtagString;

                          if (watchedPlatforms.length > 1 && activeTab) {
                            setPlatformContent({ ...platformContent, [activeTab]: newContent });
                            setValue('content', newContent); // Keep form state in sync
                          } else {
                            setValue('content', newContent);
                          }

                          setGeneratedHashtags([]); // Clear hashtags after adding all
                          toast({
                            title: "All Hashtags Added",
                            description: "All hashtags have been added to your content.",
                          });
                        }}
                        className="px-3 py-1 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-xs font-medium"
                      >
                        Add All
                      </button>
                      <button
                        type="button"
                        onClick={() => setGeneratedHashtags([])}
                        className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {generatedHashtags.map((hashtag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          const currentContent = watchedPlatforms.length > 1 && activeTab
                            ? platformContent[activeTab] ?? ''
                            : watch('content') ?? '';
                          const newContent = currentContent ? `${currentContent} ${hashtag}` : hashtag;

                          if (watchedPlatforms.length > 1 && activeTab) {
                            setPlatformContent({ ...platformContent, [activeTab]: newContent });
                            setValue('content', newContent); // Keep form state in sync
                          } else {
                            setValue('content', newContent);
                          }

                          toast({
                            title: "Hashtag Added",
                            description: `${hashtag} has been added to your content.`,
                          });
                        }}
                        className="px-2 py-1 bg-purple-100/50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200/50 dark:hover:bg-purple-900/50 transition-colors text-xs font-medium"
                      >
                        {hashtag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Publishing & Schedule Section */}
            <div id="scheduling" className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
              <div className="relative p-3">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center">
                    <Clock className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200">Publish</h3>
                </div>

                <div className="space-y-2">
                  {/* Compact Status Selection */}
                  <div className="flex space-x-1">
                    {[
                      { value: 'draft', label: 'Draft', icon: FileText },
                      { value: 'scheduled', label: 'Schedule', icon: Clock },
                      { value: 'published', label: 'Now', icon: Send }
                    ].map((status) => (
                      <label
                        key={status.value}
                        className={`flex-1 flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all text-xs font-medium ${watchedStatus === status.value
                          ? 'border-emerald-500 bg-emerald-100 dark:bg-emerald-500/30 text-emerald-700 dark:text-emerald-200 shadow-md'
                          : 'border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
                          }`}
                      >
                        <input
                          {...register('status')}
                          type="radio"
                          value={status.value}
                          className="sr-only"
                        />
                        <status.icon className="w-3 h-3 mr-1" />
                        {status.label}
                      </label>
                    ))}
                  </div>

                  {/* Publish Now Warning */}
                  {watchedStatus === 'published' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200/50 dark:border-green-700/50 rounded-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <Send className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">
                          Publish Immediately
                        </p>
                      </div>
                      <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                        Your post will be published to all selected social media platforms immediately after creation.
                      </p>
                    </motion.div>
                  )}

                  {/* Compact Schedule Date & Time */}
                  {watchedStatus === 'scheduled' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Schedule Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          {...register('scheduled_at')}
                          className="w-full px-3 py-2 bg-slate-100/50 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/30 rounded-lg text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 text-sm [color-scheme:light] dark:[color-scheme:dark]"
                          min={new Date().toISOString().slice(0, 16)}
                        />
                      </div>
                      {errors.scheduled_at && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors.scheduled_at.message}
                        </p>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-slate-300/70 dark:border-slate-600/50 bg-white dark:bg-slate-700/50 hover:bg-slate-100/50 dark:hover:bg-slate-600/50 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-all duration-300 text-sm"
              >
                Cancel
              </button>
              <PublishButton
                status={watchedStatus}
                mode={mode}
                isLoading={isLoading}
                type="submit"
              />
            </div>
          </form>
        </div>

        {/* Right Panel - Preview */}
        <div className="xl:col-span-7">
          <div className="xl:sticky xl:top-8">
            <div className="relative overflow-hidden rounded-2xl xl:rounded-3xl bg-white dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 shadow-xl xl:shadow-2xl min-h-[400px] xl:min-h-[600px]">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>

              {/* Preview Header */}
              <div className="relative border-b border-slate-200/50 dark:border-slate-600/30 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-200">Post Preview</h3>
                  </div>
                </div>
              </div>

              {/* Preview Content */}
              <div className="relative p-4 flex flex-col items-center justify-center">
                {selectedMediaFiles.length > 0 ? (
                  <div className="w-full flex flex-col items-center">
                    {/* Platform Selector */}
                    {watchedPlatforms && watchedPlatforms.length > 0 && (
                      <div className="mb-6 flex items-center space-x-2">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Preview on:</span>
                        <div className="flex space-x-2">
                          {watchedPlatforms.map((platformId) => {
                            const platform = platforms.find(p => p.id === platformId);
                            const contentType = selectedContentTypes[platformId]?.[0] || 'feed';
                            return (
                              <button
                                key={platformId}
                                onClick={() => setPreviewPlatform(platformId as 'instagram' | 'facebook' | 'youtube' | 'twitter')}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  previewPlatform === platformId
                                    ? 'bg-indigo-500 text-white'
                                    : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-500'
                                }`}
                              >
                                {platform?.name} {contentType !== 'feed' && contentType !== 'post' && contentType !== 'tweet' && (
                                  <span className="ml-1 opacity-75">({contentType})</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Mobile Preview */}
                    <MobilePreview
                      platform={previewPlatform || (watchedPlatforms?.[0] as 'instagram' | 'facebook' | 'youtube' | 'twitter') || 'instagram'}
                      contentType={selectedContentTypes[previewPlatform || watchedPlatforms?.[0] || 'instagram']?.[0] || 'feed'}
                      title={watch('title')}
                      content={(() => {
                        const currentPlatform = previewPlatform || watchedPlatforms?.[0];
                        return watchedPlatforms.length > 1 && currentPlatform && platformContent[currentPlatform] 
                          ? platformContent[currentPlatform] 
                          : watch('content');
                      })()}
                      mediaFiles={selectedMediaFiles}
                      clientId={currentClient?.id || ''}
                      brandName={currentClient?.name || 'Your Brand'}
                    />
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-slate-100/50 dark:bg-slate-700/30 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Eye className="w-10 h-10 text-slate-400 dark:text-slate-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-300 mb-2">Preview Your Post</h3>
                    <p className="text-slate-500 dark:text-slate-400">Upload an image to see how your post will look</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Monthly Post Limit Reached"
        message={upgradeModalData.message}
        currentCount={upgradeModalData.currentCount}
        limit={upgradeModalData.limit}
        feature="posts"
      />
    </div>
  );
};

export default PostForm;
